import React, { useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import _ from "lodash";
import dayjs from "dayjs";

import { State } from "src/state/ducks";
import * as OrdersActions from "src/state/ducks/order/actions";
import * as NotificationActions from "src/state/ducks/notification/actions";

import { formatDateAsQuery, formatDateToDate } from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import HomeButton from "src/components/atoms/button/HomeButton";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import DatePickerForm from "src/components/organisms/form/DatePickerForm";

type Props = DefaultPageProps &
  HistoryProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const OrderBread = (props: Props) => {
  const {
    orders,
    notifications,
    login_shop_id: currentShopId,
    getDayOrders,
    getDayNotifications,
    isFetching,
    history,
  } = props;

  // 初期状態は現在の月単位でデータ読み込み
  useEffect(() => {
    const from = dayjs().startOf("month").toDate();
    const until = dayjs().endOf("month").toDate();
    getDayOrders(currentShopId, from, until);
    getDayNotifications(currentShopId, from, until);
  }, [currentShopId, getDayOrders, getDayNotifications]);

  // 注文があった日、もしくは通常焼き上がりを行った日付を取得
  const selectableDays = useMemo(() => {
    const orderDays = _(orders)
      .map((v) =>
        dayjs(formatDateToDate(v.last_pickup_time)).format("YYYY-MM-DD")
      )
      .uniq()
      .value();

    const notificationDays = _(notifications)
      .map((v) => dayjs(formatDateToDate(v.created_at)).format("YYYY-MM-DD"))
      .uniq()
      .value();

    return _(orderDays).concat(notificationDays).uniq().value();
  }, [orders, notifications]);

  const onNext = useCallback(
    (date: Date | undefined) => {
      history.push(
        `/shop/${currentShopId}/search/orders/${formatDateAsQuery(date)}`
      );
    },
    [currentShopId, history]
  );

  const onMonthChange = useCallback(
    (date: Date) => {
      const from = dayjs(date).startOf("month").toDate();
      const until = dayjs(date).endOf("month").toDate();
      getDayOrders(currentShopId, from, until);
      getDayNotifications(currentShopId, from, until);
    },
    [currentShopId, getDayOrders, getDayNotifications]
  );

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <Heading tag="h1" text="注文状況を確認する" />
        <p className="lead">
          注文状況を確認したい日付を選択してください
          <br />
          ※灰色の日は焼き上がりの通知自体を行っていません
        </p>
        <div className="item">
          <DatePickerForm
            onNext={onNext}
            onMonthChange={onMonthChange}
            selectableDays={selectableDays}
          />
        </div>
        <div className="item">
          <HomeButton />
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  > .lead {
    font-size: 12px;
  }
  > .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
`;

const mapStateToProps = (state: State) => ({
  notifications: state.notification.filterByDate.resource,
  orders: state.order.filterByDate.resource,
  isFetching:
    state.order.filterByDate.isFetching ||
    state.notification.filterByDate.isFetching,
  error: state.order.filterByDate.apiError,
  ordersError: state.order.filterByDate.apiError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getDayOrders: (id: string, startDate: Date, endDate: Date) =>
    dispatch(
      OrdersActions.getDayOrders({
        shopId: id,
        startDate: startDate,
        endDate: endDate,
        target: "last_pickup_time",
        noUsers: true,
        noDetails: true,
      })
    ),
  getDayNotifications: (id: string, startDate: Date, endDate: Date) =>
    dispatch(
      NotificationActions.getDayNotifications({
        shopId: id,
        startDate: startDate,
        endDate: endDate,
        isReservation: false,
        noDetails: true,
      })
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderBread);
